import {type FC, lazy, useCallback, useEffect, useRef} from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

import type {
  ConferenceFailureDetails,
  ConferenceLifecycleExit,
  RecordingAnalyticsEventName,
  RecordingAnalyticsPayload,
} from '@yourcoach/conferences-web';

import {i18n} from '@yourcoach/i18n';
import {alert} from '@yourcoach/shared/components/alert';
import {showBusyAlert} from '@yourcoach/shared/modules/conference/stores/showBusyAlert';
import {
  showRecordConfirm,
  type ShowRecordConfirmParams,
} from '@yourcoach/shared/modules/conference/stores/showRecordConfirm';
import {logger} from '@yourcoach/shared/utils/logger';

import appStore from '@src/context/appStore';
import {routerPath} from '@src/routes/path';

import type {RouteConfig} from '../types';
import type {ConferenceRouteState} from './conferenceRouteState';

// @ts-expect-error: mp3 asset
import joinSound from '../../../join-sound.mp3';
import {Loadable} from '../components/Loadable';
import {miscRoutesPath} from './path';

const CoachesPage = Loadable(
  lazy(
    () => import('@src/pages/Coaches' /* webpackChunkName: "coaches-page" */),
  ),
);
const ConferenceLegacyRedirect: FC = () => {
  const {conferenceId} = useParams<'conferenceId'>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const qs = searchParams.toString();

  return (
    <Navigate
      replace
      state={location.state}
      to={`/conferences/v2/${conferenceId}${qs ? `?${qs}` : ''}`}
    />
  );
};
const ConferenceV2PageImpl = Loadable(
  lazy(() =>
    import(
      '@yourcoach/conferences-web' /* webpackChunkName: "conference-v2-page" */
    ).then(module => ({default: module.VideoConferencePage})),
  ),
);
const PhoneConferencePageImpl = Loadable(
  lazy(() =>
    import(
      '@yourcoach/phone-conferences-web' /* webpackChunkName: "phone-conference-page" */
    ).then(module => ({default: module.PhoneConferencePage})),
  ),
);
const ConnectStripePage = Loadable(
  lazy(
    () =>
      import(
        '@src/pages/ConnectStripe' /* webpackChunkName: "connect-stripe-page" */
      ),
  ),
);
const GoogleConnectPage = Loadable(
  lazy(() =>
    import(
      '@src/v2/pages/GoogleConnect' /* webpackChunkName: "google-connect-page" */
    ).then(module => ({default: module.GoogleConnectPage})),
  ),
);
const GoalsPage = Loadable(
  lazy(() => import('@src/pages/Goals' /* webpackChunkName: "goals-page" */)),
);
const OnboardingPage = Loadable(
  lazy(
    () =>
      import('@src/pages/Onboarding' /* webpackChunkName: "onboarding-page" */),
  ),
);
const TutorialsPage = Loadable(
  lazy(
    () =>
      import('@src/pages/Tutorials' /* webpackChunkName: "tutorials-page" */),
  ),
);
const TutorialsPublicPage = Loadable(
  lazy(
    () =>
      import(
        '@src/pages/TutorialsPublic' /* webpackChunkName: "tutorials-public-page" */
      ),
  ),
);
const FirstUserRegister = Loadable(
  lazy(
    () =>
      import(
        '@src/modules/FirstUserRegister/FirstUserRegister' /* webpackChunkName: "first-user-register-page" */
      ),
  ),
);
const UserProfilePage = Loadable(
  lazy(() =>
    import(
      '@src/v2/pages/UserProfile/UserProfile.page' /* webpackChunkName: "user-profile-page" */
    ).then(module => ({default: module.UserProfilePage})),
  ),
);

const isMediaEnabled = (value: '0' | '1' | undefined) => value !== '0';

const useConferenceId = () => {
  const {conferenceId} = useParams<'conferenceId'>();

  if (!conferenceId) {
    throw new Error('Missing conferenceId route param');
  }

  return conferenceId;
};

const getLifecycleExitMessage = (exit: ConferenceLifecycleExit) => {
  switch (exit.reason) {
    case 'ended':
      return i18n.t('shared:Conference.conference_ended_message');
    case 'expired':
      return i18n.t('shared:Conference.conference_expired_message');
    case 'invalid':
      return i18n.t('shared:Conference.conference_ended_message');
    case 'not_started':
      return i18n.t('shared:Conference.conference_not_started_message');
    case 'too_early':
      return i18n.t('shared:Conference.conference_upcoming_message', {
        date: exit.conference?.start_date,
      });
  }
};

/**
 * Every way out of a conference route. A bare `navigate(-1)` lands on whatever
 * entry sits behind the conference — when that was a stray duplicate of the
 * conference URL the page stayed mounted in its final state and went blank.
 */
const useLeaveConference = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as ConferenceRouteState | null)?.returnTo;

  return useCallback(() => {
    if (returnTo) {
      navigate(returnTo, {replace: true});

      return;
    }

    // The router stamps its own history entries with `idx`; 0 means the
    // conference opened this tab (a deep link), so there is nothing to go back to.
    const historyIndex =
      (window.history.state as null | {idx?: number})?.idx ?? 0;

    if (historyIndex > 0) {
      navigate(-1);

      return;
    }

    navigate(routerPath.yourSpace.$buildPath({}), {replace: true});
  }, [navigate, returnTo]);
};

const useLifecycleExit = (leaveConference: () => void) =>
  useCallback(
    async (exit: ConferenceLifecycleExit) => {
      await alert.show({
        message: getLifecycleExitMessage(exit),
        title: i18n.t('shared:message.error'),
      });
      leaveConference();
    },
    [leaveConference],
  );

/**
 * Recording-consent plumbing, shared by the video and phone conference pages
 * so a phone conference shows the same consent dialog a video one does.
 */
const useRecordingConsentHandlers = () => {
  const onRecordingConsentView = useCallback(
    () => logger.event('session_recording_permission_view'),
    [],
  );
  const onRecordingConsentTap = useCallback(
    (granted: 'no' | 'yes') =>
      logger.event('session_recording_permission_tap', {granted}),
    [],
  );
  const onRecordingAnalyticsEvent = useCallback(
    (
      eventName: RecordingAnalyticsEventName,
      payload?: RecordingAnalyticsPayload,
    ) => logger.event(eventName, payload),
    [],
  );
  const onRecordingConsent = useCallback(
    ({
      mode,
      onAccept,
      onDecline,
      titleKey,
    }: {
      mode: ShowRecordConfirmParams['declineMode'];
      onAccept: () => void;
      onDecline: () => void;
      titleKey: ShowRecordConfirmParams['titleKey'];
    }) => {
      showRecordConfirm({
        declineMode: mode,
        onAllow: onAccept,
        onDecline,
        onShow: () => {},
        titleKey,
      });
    },
    [],
  );

  return {
    onRecordingAnalyticsEvent,
    onRecordingConsent,
    onRecordingConsentTap,
    onRecordingConsentView,
  };
};

const ConferenceV2Page: FC = () => {
  const conferenceId = useConferenceId();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const leaveConference = useLeaveConference();
  const onLifecycleExit = useLifecycleExit(leaveConference);
  const navigate = useNavigate();
  const mediaSearchParamsRef = useRef(new URLSearchParams(searchParams));

  useEffect(() => {
    mediaSearchParamsRef.current = new URLSearchParams(searchParams);
  }, [searchParams]);

  const updateMediaSearchParam = useCallback(
    (key: 'a' | 'v', isEnabled: boolean) => {
      const nextSearchParams = new URLSearchParams(
        mediaSearchParamsRef.current,
      );

      nextSearchParams.set(key, isEnabled ? '1' : '0');
      mediaSearchParamsRef.current = nextSearchParams;

      navigate(
        {
          hash: location.hash,
          pathname: location.pathname,
          search: `?${nextSearchParams.toString()}`,
        },
        {
          preventScrollReset: true,
          replace: true,
          state: location.state,
        },
      );
    },
    [location.hash, location.pathname, location.state, navigate],
  );

  useEffect(() => {
    logger.event('video_screen_view');
  }, []);

  const a = searchParams.get('a') === '0' ? '0' : undefined;
  const v = searchParams.get('v') === '0' ? '0' : undefined;
  const onWaitingRoomView = useCallback(
    () => logger.event('session_preview_view'),
    [],
  );
  const onWaitingRoomClose = useCallback(
    () => logger.event('session_preview_close_tap'),
    [],
  );
  const onBusyAlert = useCallback(() => showBusyAlert(), []);
  const pingConnectivity = useCallback(
    () =>
      fetch('/api/v2/health', {method: 'HEAD'})
        .then(() => true)
        .catch(() => false),
    [],
  );
  const onOffline = useCallback(async () => {
    await alert.show({
      message: i18n.t('shared:Conference.not_connected_error'),
      title: i18n.t('shared:message.error'),
    });
    leaveConference();
  }, [leaveConference]);
  const onConferenceFailure = useCallback(
    async (failure: ConferenceFailureDetails) => {
      const {code, error, kind, message: rawMessage} = failure;
      const isFatal =
        kind === 'permission_denied' || kind === 'hardware_unavailable';
      const message =
        kind === 'permission_denied'
          ? i18n.t('shared:Conference.media_access_denied_error.web')
          : kind === 'hardware_unavailable'
            ? i18n.t('shared:unable_to_access_media')
            : kind === 'poor_network'
              ? i18n.t('shared:conference_poor_connection')
              : i18n.t('shared:Conference.not_connected_error');

      logger.event('video_loaded_fail', {
        code,
        message: rawMessage,
        reason: kind,
      });
      logger.error(error);
      await alert.show({
        message: `${message}\n\n${kind}: ${rawMessage}${code !== rawMessage ? ` (${code})` : ''}`,
        title: i18n.t('shared:message.error'),
      });

      if (isFatal) leaveConference();
    },
    [leaveConference],
  );
  const onJoinSound = useCallback(() => {
    try {
      const player = appStore.audio;

      if (!player) return;

      player.src = joinSound;
      player.currentTime = 0;
      player.play().catch(e => logger.error(e));
    } catch (e) {
      logger.error(e);
    }
  }, []);
  const onVideoLoaded = useCallback(
    () => logger.event('video_loaded_success'),
    [],
  );
  const onAudioTap = useCallback(
    (type: 'mute' | 'unmute') => {
      logger.event('audio_tap', {type});
      updateMediaSearchParam('a', type === 'unmute');
    },
    [updateMediaSearchParam],
  );
  const onCameraTap = useCallback(
    (type: 'off' | 'on') => {
      logger.event('camera_tap', {type});
      updateMediaSearchParam('v', type === 'on');
    },
    [updateMediaSearchParam],
  );
  const onVideoEndTap = useCallback(
    (duration: number) => logger.event('video_end_tap', {duration}),
    [],
  );
  const {
    onRecordingAnalyticsEvent,
    onRecordingConsent,
    onRecordingConsentTap,
    onRecordingConsentView,
  } = useRecordingConsentHandlers();

  return (
    <ConferenceV2PageImpl
      conferenceId={conferenceId}
      isAudioEnabled={isMediaEnabled(a)}
      isVideoEnabled={isMediaEnabled(v)}
      onAudioTap={onAudioTap}
      onBusyAlert={onBusyAlert}
      onCameraTap={onCameraTap}
      onConferenceDone={leaveConference}
      onConferenceFailure={onConferenceFailure}
      onJoinSound={onJoinSound}
      onLifecycleExit={onLifecycleExit}
      onOffline={onOffline}
      onRecordingAnalyticsEvent={onRecordingAnalyticsEvent}
      onRecordingConsent={onRecordingConsent}
      onRecordingConsentTap={onRecordingConsentTap}
      onRecordingConsentView={onRecordingConsentView}
      onVideoEndTap={onVideoEndTap}
      onVideoLoaded={onVideoLoaded}
      onWaitingRoomClose={onWaitingRoomClose}
      onWaitingRoomView={onWaitingRoomView}
      pingConnectivity={pingConnectivity}
    />
  );
};

const PhoneConferencePage: FC = () => {
  const conferenceId = useConferenceId();
  // The machine ignores STOP from its error state, so leaving a failed phone
  // conference routes away instead — the user can retry from the list. The
  // same navigation runs once a finished session reaches `done`.
  const leaveConference = useLeaveConference();
  const onLifecycleExit = useLifecycleExit(leaveConference);
  const {
    onRecordingAnalyticsEvent,
    onRecordingConsent,
    onRecordingConsentTap,
    onRecordingConsentView,
  } = useRecordingConsentHandlers();

  return (
    <PhoneConferencePageImpl
      conferenceId={conferenceId}
      onConferenceDone={leaveConference}
      onLeave={leaveConference}
      onLifecycleExit={onLifecycleExit}
      onRecordingAnalyticsEvent={onRecordingAnalyticsEvent}
      onRecordingConsent={onRecordingConsent}
      onRecordingConsentTap={onRecordingConsentTap}
      onRecordingConsentView={onRecordingConsentView}
    />
  );
};

export const miscRoutes = [
  {
    element: <CoachesPage key="community" />,
    path: miscRoutesPath.community.$path(),
  },
  {
    element: <CoachesPage key="my-coaches" />,
    path: miscRoutesPath.myCoaches.$path(),
  },
  {
    element: <FirstUserRegister />,
    path: miscRoutesPath.userProfileReg.$path(),
  },
  {
    element: <GoalsPage />,
    path: miscRoutesPath.goals.$path(),
  },
  {
    element: <OnboardingPage />,
    path: miscRoutesPath.onboarding.$path(),
  },
  {
    element: <ConferenceLegacyRedirect />,
    path: miscRoutesPath.conference.$path(),
  },
  {
    element: <ConferenceV2Page />,
    path: miscRoutesPath.conferenceV2.$path(),
  },
  {
    element: <PhoneConferencePage />,
    path: miscRoutesPath.phoneConference.$path(),
  },
  {
    element: <ConnectStripePage />,
    path: miscRoutesPath.connectStripe.$path(),
  },
  {
    element: <GoogleConnectPage />,
    path: miscRoutesPath.googleConnect.$path(),
  },
  {
    element: <TutorialsPage />,
    path: miscRoutesPath.tutorials.$path(),
    publicElement: <TutorialsPublicPage />,
  },
  {
    element: <UserProfilePage />,
    path: miscRoutesPath.userProfile.$path(),
  },
] satisfies RouteConfig[];
